import {
  useGetItemsByRestaurantId,
  useGetMenuGroupsByRestaurantId,
  useSelectedLocation,
  useSelectedTenant,
} from "@order-up/client/vite";
import { Skeleton } from "@order-up/common";
import type { IMenuGroup, Item, MenuGroup } from "@order-up/models";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MenuGroup as MenuGroupComponent } from "./MenuGroup";

interface MenuDisplayProps {
  parentMenuGroupId?: number;
  selectedMenuGroup?: IMenuGroup | null;
  onBackToMenuSelection?: () => void;
  /** Maximum categories to show per page (default: auto-detect based on screen) */
  maxCategoriesPerPage?: number;
  /** Seconds to display each page before rotating (default: 15) */
  pageRotationSeconds?: number;
}

/**
 * The menu body while its data is in flight.
 *
 * EXPORTED so `menu-loading-fallback.tsx` renders this one definition rather
 * than the hand-kept copy it used to carry — its own header said the copy was
 * "kept in sync", which is a promise no check enforced. Declared outside the
 * component to avoid recreation on each render.
 */
export const MenuSkeleton = () => (
  <div className="menu-display__content menu-display__content--dual">
    {Array.from({ length: 2 }).map((_, groupIndex) => (
      <section key={groupIndex} className="menu-group">
        {/* Group Header Skeleton */}
        <div className="menu-group__header">
          <div className="menu-group__title">
            <Skeleton className="h-8 w-48 mb-2" />
          </div>
          <div className="menu-group__divider"></div>
          <div className="menu-group__description">
            <Skeleton className="h-4 w-64 mb-4" />
          </div>
        </div>

        {/* Group Content Skeleton */}
        <div className="menu-group__content">
          {Array.from({ length: 4 }).map((_, itemIndex) => {
            // Mix single and size-priced items in skeleton
            const hasSizes = itemIndex % 3 === 0;

            return (
              // 3-row grid skeleton layout
              <div key={itemIndex} className="menu-item">
                {/* Row 1-2: Image - smaller size */}
                <div className="menu-item__image">
                  <Skeleton className="w-[60px] h-[60px] rounded-lg" />
                </div>

                {/* Row 1: Name */}
                <div className="menu-item__name">
                  <Skeleton className="h-4 w-4/5" />
                </div>

                {/* Row 2: Spacer and Pricing */}
                <div className="menu-item__spacer"></div>
                <div className="menu-item__pricing">
                  {hasSizes ? (
                    // Size pricing skeleton
                    <div className="menu-item__size-prices">
                      <Skeleton className="h-6 w-16 rounded" />
                      <Skeleton className="h-6 w-18 rounded" />
                      <Skeleton className="h-6 w-14 rounded" />
                    </div>
                  ) : (
                    // Single price skeleton
                    <Skeleton className="h-6 w-12 rounded inline-block" />
                  )}
                </div>

                {/* Row 3: Description - full width */}
                <div className="menu-item__description">
                  <Skeleton className="h-3 w-full mb-1" />
                  <Skeleton className="h-3 w-3/4" />
                </div>
              </div>
            );
          })}
        </div>
      </section>
    ))}
  </div>
);

// Color variants for category headers
const HEADER_VARIANTS = ["primary", "secondary", "accent", "chart"] as const;

export const MenuDisplay: React.FC<MenuDisplayProps> = ({
  parentMenuGroupId,
  selectedMenuGroup: propSelectedMenuGroup,
  onBackToMenuSelection,
  maxCategoriesPerPage,
  pageRotationSeconds = 15,
}) => {
  const { tenantId } = useSelectedTenant();
  const { restaurantId: locationNumericId, restaurant: selectedLocation } =
    useSelectedLocation();

  // Call all hooks before any early returns
  const {
    data: menuGroups = [],
    isLoading: isMenuGroupsLoading,
    error: menuGroupsError,
  } = useGetMenuGroupsByRestaurantId(tenantId || 0, locationNumericId || 0);
  const {
    data: items = [],
    isLoading: isItemsLoading,
    error: itemsError,
  } = useGetItemsByRestaurantId(tenantId || 0, locationNumericId || 0);

  // Use prop selectedMenuGroup if provided
  const selectedMenuGroup = propSelectedMenuGroup;

  const [minLoadTimeElapsed, setMinLoadTimeElapsed] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [categoriesPerPage, setCategoriesPerPage] = useState(
    maxCategoriesPerPage || 4,
  );

  // Detect optimal categories per page based on screen width
  useEffect(() => {
    if (maxCategoriesPerPage) {
      setTimeout(() => {
        setCategoriesPerPage(maxCategoriesPerPage);
      }, 0);
      return;
    }

    const updateCategoriesPerPage = () => {
      const width = window.innerWidth;
      if (width >= 2000) setCategoriesPerPage(4);
      else if (width >= 1400) setCategoriesPerPage(3);
      else if (width >= 900) setCategoriesPerPage(2);
      else setCategoriesPerPage(1);
    };

    updateCategoriesPerPage();
    window.addEventListener("resize", updateCategoriesPerPage);
    return () => window.removeEventListener("resize", updateCategoriesPerPage);
  }, [maxCategoriesPerPage]);

  // Filter menu groups based on selection
  const filteredGroups = menuGroups.filter((group) => {
    if (parentMenuGroupId) {
      // If parentMenuGroupId prop is provided, use it (for nested views)
      return group.parentIds && group.parentIds.includes(parentMenuGroupId);
    }

    if (selectedMenuGroup) {
      // If a specific menu group is selected, show only its children (sub-groups)
      // Don't show the parent group itself to avoid redundancy
      return (
        group.parentIds &&
        group.parentIds.includes(selectedMenuGroup.menuGroupId!)
      );
    }

    // Default: show only top-level groups (when viewing "All Menus" or no selection)
    return !group.parentIds || group.parentIds.length === 0;
  });

  /**
   * The ROOT→LEAF chain above the groups being rendered.
   *
   * This is what makes the displayed prices trustworthy. `resolveItemPrice`
   * walks ROOT→LEAF and the first group with an active pricing strategy wins,
   * so a chain that starts at the wrong depth resolves to the wrong price.
   *
   * It is a COMPLETE chain, not a best effort: `MenuSelectionWizard` offers
   * only parentless groups (`useParentMenuGroups`), so `selectedMenuGroup` is
   * always a DAG root, and the nested-view `parentMenuGroupId` is supplied by
   * the caller that navigated into it.
   */
  // Plain const, not useMemo: this app compiles with the React Compiler, which
  // memoizes this itself. A manual useMemo here is REJECTED by the compiler
  // (the hand-written dep `selectedMenuGroup?.menuGroupId` is narrower than the
  // inferred `selectedMenuGroup`), and a rejected memo opts the whole component
  // out of compilation — strictly worse than letting the compiler do it.
  const ancestorPath: number[] = parentMenuGroupId
    ? [parentMenuGroupId]
    : selectedMenuGroup?.menuGroupId != null
      ? [selectedMenuGroup.menuGroupId]
      : [];

  // Group items by menu group
  // Explicitly type to allow mixing MenuGroup class instances with IMenuGroup interface
  let itemsByGroup: Array<{
    group: IMenuGroup;
    items: Item[];
    subgroups: MenuGroup[];
    groupPath: number[];
  }> = filteredGroups.map((group) => {
    const groupItems = items.filter((item) =>
      group.items?.some((itemRef) => itemRef.itemId === item.itemId),
    );

    const subgroups = menuGroups.filter(
      (subgroup) =>
        subgroup.parentIds && subgroup.parentIds.includes(group.menuGroupId!),
    );

    return {
      group,
      items: groupItems,
      subgroups,
      groupPath:
        group.menuGroupId != null
          ? [...ancestorPath, group.menuGroupId]
          : [...ancestorPath],
    };
  });

  // Special case: If we selected a menu group but it has no children,
  // show its items directly without the redundant parent group header
  if (selectedMenuGroup && filteredGroups.length === 0) {
    const selectedGroupItems = items.filter((item) =>
      selectedMenuGroup.items?.some(
        (itemRef) => itemRef.itemId === item.itemId,
      ),
    );

    if (selectedGroupItems.length > 0) {
      itemsByGroup = [
        {
          group: selectedMenuGroup,
          items: selectedGroupItems,
          subgroups: [],
          // The selected group IS the leaf here — it has no children — so the
          // chain ends at it rather than being extended by one.
          groupPath: ancestorPath,
        },
      ];
    }
  }

  // Split categories into pages
  const pages = useMemo(() => {
    const result: (typeof itemsByGroup)[] = [];
    for (let i = 0; i < itemsByGroup.length; i += categoriesPerPage) {
      result.push(itemsByGroup.slice(i, i + categoriesPerPage));
    }
    return result;
  }, [itemsByGroup, categoriesPerPage]);

  const totalPages = pages.length;
  const needsRotation = totalPages > 1;

  // Page rotation timer
  useEffect(() => {
    if (!needsRotation || !minLoadTimeElapsed) return;

    const timer = setInterval(() => {
      setCurrentPage((prev) => (prev + 1) % totalPages);
    }, pageRotationSeconds * 1000);

    return () => clearInterval(timer);
  }, [needsRotation, totalPages, pageRotationSeconds, minLoadTimeElapsed]);

  // Reset to first page when data changes
  useEffect(() => {
    setTimeout(() => {
      setCurrentPage(0);
    }, 0);
  }, [itemsByGroup.length, categoriesPerPage]);

  // Dynamic layout classes based on number of groups on current page
  const getContentClassName = useCallback((groupCount: number) => {
    const baseClass = "menu-display__content";

    if (groupCount === 1) return `${baseClass} ${baseClass}--single`;
    if (groupCount === 2) return `${baseClass} ${baseClass}--dual`;
    if (groupCount === 3) return `${baseClass} ${baseClass}--triple`;
    return `${baseClass} ${baseClass}--quad`;
  }, []);

  const isLoading = isMenuGroupsLoading || isItemsLoading;
  const error = menuGroupsError || itemsError;

  // Keep skeleton visible for at least 1 second to prevent flashing
  useEffect(() => {
    if (!isLoading && itemsByGroup.length > 0) {
      // When loading completes, wait minimum time before hiding skeleton
      const timer = setTimeout(() => {
        setMinLoadTimeElapsed(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
    // Reset when new loading starts
    return () => setMinLoadTimeElapsed(false);
  }, [isLoading, itemsByGroup.length]);

  const showSkeleton = isLoading || !minLoadTimeElapsed;

  // Exit early if no selection (after all hooks are called)
  if (!tenantId || !locationNumericId || !selectedLocation) {
    return (
      <div className="menu-display-message">Restaurant selection required</div>
    );
  }

  // Show error state
  if (error)
    return <div className="menu-display-message">Menu unavailable</div>;

  // Show "coming soon" only if we have no data after loading
  if (!isLoading && itemsByGroup.length === 0) {
    return <div className="menu-display-message">Menu coming soon</div>;
  }

  // Use restaurant name from context
  const locationName = selectedLocation.name || "Restaurant";
  const currentPageData = pages[currentPage] || [];

  return (
    <div className="menu-display">
      {/* Brand Bar - Always visible (non-database data) */}
      <div className="menu-display__header">
        <div className="menu-display__header-content">
          {onBackToMenuSelection && (
            <button
              onClick={onBackToMenuSelection}
              className="menu-display__back-button"
            >
              ← Back to Menu Selection
            </button>
          )}
          <div className="menu-display__brand-bar">
            <span className="menu-display__brand-name">{locationName}</span>
            <span className="menu-display__brand-separator"> • </span>
            <span className="menu-display__brand-tagline">Menu</span>
          </div>
          {/* Empty spacer for centering when back button exists */}
          {onBackToMenuSelection && <div style={{ width: "180px" }} />}
        </div>
      </div>

      {/* Menu Grid - Show skeleton while loading or actual data when loaded */}
      {showSkeleton || isLoading ? (
        <MenuSkeleton />
      ) : (
        <>
          <div className={getContentClassName(currentPageData.length)}>
            {currentPageData.map(({ group, items, subgroups, groupPath }, index) => {
              // Calculate global index for consistent color assignment
              const globalIndex = currentPage * categoriesPerPage + index;
              const variant =
                HEADER_VARIANTS[globalIndex % HEADER_VARIANTS.length];

              return (
                <MenuGroupComponent
                  key={group.menuGroupId || index}
                  group={group}
                  items={items}
                  subgroups={subgroups}
                  variant={variant}
                  groupPath={groupPath}
                  allMenuGroups={menuGroups}
                />
              );
            })}
          </div>

          {/* Page indicators - only show if multiple pages */}
          {needsRotation && (
            <>
              <div className="menu-display__page-indicators">
                {pages.map((_, index) => (
                  <button
                    key={index}
                    className={`menu-display__page-dot ${index === currentPage ? "menu-display__page-dot--active" : ""}`}
                    onClick={() => setCurrentPage(index)}
                    aria-label={`Go to page ${index + 1}`}
                  />
                ))}
              </div>

              {/* Progress bar showing time until next page */}
              <div
                className="menu-display__page-progress"
                key={currentPage} // Reset animation on page change
                style={
                  {
                    "--page-duration": `${pageRotationSeconds}s`,
                  } as React.CSSProperties
                }
              />
            </>
          )}
        </>
      )}
    </div>
  );
};
